import React, { useState } from 'react';
import { Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import StarSolidIcon from '../../assets/svg/StarSolidIcon';
import { useTheme } from '@mui/material/styles';
import StyledLinearProgress from '../../components/LinearProgress';
import { TaskExtended } from '../../../shared/types/Task';
import CloseIcon from '@mui/icons-material/Close';
import { newUiColorTokens } from '../../theme/themeTokens';
import { spacingTokens } from '../../theme/spacingTokens';
import { typographyTokens } from '../../theme/typographyTokens';
import CurrentPhaseIcon from '../../../frontend/assets/svg/CurrentPhaseIcon';

type ProgressProps = {
  title: string;
  sepPhase?: TaskExtended[] | null;
  completedTasks?: TaskExtended[] | null;
  sepFinish?: boolean;
  sepPhaseLen?: number;
  completedLen?: number;
  phaseTitle?: string;
  onClick?: () => void;
  isSEPPhase?: boolean;
  noTasksInImplementPhase?: boolean;
};

const PhaseProgress: React.FC<ProgressProps> = ({
  title,
  sepPhase,
  completedTasks,
  sepFinish,
  sepPhaseLen,
  completedLen,
  phaseTitle,
  isSEPPhase,
  onClick,
  noTasksInImplementPhase
}: ProgressProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isTaskDetailsModalOpen, setIsTaskDetailsModalOpen] = useState(false);
  const [selectedPhaseTasks, setSelectedPhaseTasks] = useState<TaskExtended[]>([]);
  const openTaskDetailsModal = (phaseTasks: TaskExtended[] | null | undefined) => {
    const tasksArray = phaseTasks ? (Array.isArray(phaseTasks) ? phaseTasks : [phaseTasks]) : [];
    setSelectedPhaseTasks(tasksArray);
    setIsTaskDetailsModalOpen(true);
  };


  const closeTaskDetailsModal = () => {
    setIsTaskDetailsModalOpen(false);
    setSelectedPhaseTasks([]);
  };
  const completed =
    completedLen !== undefined ? completedLen : completedTasks?.length;
  const sepPhaseLength =
    sepPhaseLen !== undefined ? sepPhaseLen : sepPhase?.length;
  const status: boolean =
    completed === sepPhaseLength && sepPhase !== null ? true : false;
  const unStarted: boolean =
    (completed === 0 && completedTasks !== null) || completed === undefined;
  const progressBarValue =
    completed !== undefined &&
      sepPhaseLength !== undefined &&
      sepPhaseLength !== 0
      ? (completed / sepPhaseLength) * 100
      : (completed === null && 100) || 0;

  return (
    <>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '10px',
          width: '97.8%',
        }}
      >
        {/* Row 1: Phase Title (centered) */}
        <Box style={{ textAlign: 'center', marginBottom: '13px', display: 'flex', alignItems: 'center', gap: spacingTokens.xs }}>
          {isSEPPhase ? (
            <Chip
              icon={
                <CurrentPhaseIcon circleColor={newUiColorTokens.prime.blue} />
              }
              label={title}
              variant="outlined"
              size="small"
              sx={{
                px: 0,
                py: 1.5,
                fontSize: typographyTokens.fontSizes.body,
                fontWeight: typographyTokens.fontWeights.semibold,
                borderColor: isDarkMode ? newUiColorTokens.prime.blue : newUiColorTokens.content.default,
                color: isDarkMode ? newUiColorTokens.prime.blue : newUiColorTokens.content.default,
                '& .MuiChip-icon': {
                  margin: 0,
                  marginLeft: spacingTokens.xs,
                  marginRight: '1px',
                  marginTop: '-1px',
                },
                '& .MuiChip-label': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '5px',
                  paddingRight: '7px'
                },
                '& .MuiChip-root': {
                  height: 28,
                },
              }}
            />
          ) : (
            <span style={{ fontSize: typographyTokens.fontSizes.body, fontWeight: typographyTokens.fontWeights.semibold, color: isDarkMode ? newUiColorTokens.prime.blue : newUiColorTokens.content.default, marginBottom: spacingTokens.xs, marginTop: spacingTokens.xs, paddingLeft: '0px' }}>{title}</span>
          )}
        </Box>

        {/* Row 2: Progress Bar with Icons */}
        <Box
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            marginBottom: spacingTokens.xs,
            marginTop: spacingTokens.xs,
          }}
        >
          <StyledLinearProgress
            variant="determinate"
            complete={status}
            theme={theme}
            value={progressBarValue}
            sx={{ m: -0.4, flexGrow: 1, height: 8, borderRadius: 1 }}
            onClick={() => openTaskDetailsModal(sepPhase)}
          />

          {/* {sepPhaseLen === undefined && (
                <FinishSepIconToggle title={title} sepFinish={sepFinish!} noTasksInImplementPhase={noTasksInImplementPhase} />
              )} */}
        </Box>

        {/* Row 3: Task Count (centered) */}
        <Box style={{ textAlign: 'center', marginTop: spacingTokens.md }}>
          <span
            style={{ fontSize: typographyTokens.fontSizes.sm, cursor: 'pointer', color: isDarkMode ? newUiColorTokens.prime.blue : newUiColorTokens.content.default }}
            onClick={() => openTaskDetailsModal(sepPhase)}
          >
            {sepPhaseLength && sepPhaseLength > 0
              ? `${completed ?? 0}/${sepPhaseLength} Tasks Complete`
              : 'No Tasks Enabled'}
          </span>
        </Box>
      </Box>

      <Dialog open={isTaskDetailsModalOpen} onClose={closeTaskDetailsModal}>
        <DialogTitle sx={{
          color: isDarkMode ? theme.palette.white.main : theme.palette.content.default
        }}>
          {selectedPhaseTasks.length > 0 ? `${title} Tasks` : 'No Tasks'}
          <IconButton
            edge="end"
            color="primary"
            size="small"
            onClick={closeTaskDetailsModal}
            aria-label="close"
            sx={{ position: 'absolute', top: spacingTokens.sm, right: spacingTokens.sm }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table sx={{ color: isDarkMode ? theme.palette.white.main : theme.palette.prime.blue }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%', textAlign: 'left', color: isDarkMode ? theme.palette.white.main : theme.palette.content.default }} align="left">Task</TableCell>
                <TableCell sx={{ width: '30%', textAlign: 'left', color: isDarkMode ? theme.palette.white.main : theme.palette.content.default }} align="left">Department</TableCell>
                <TableCell sx={{ width: '30%', textAlign: 'left', color: isDarkMode ? theme.palette.white.main : theme.palette.content.default }} align="left">Status</TableCell>
                <TableCell sx={{ width: '30%', textAlign: 'left', color: isDarkMode ? theme.palette.white.main : theme.palette.content.default }} align="left">Assigned to</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedPhaseTasks.map((task) => (
                <TableRow key={task.id}>
                  <TableCell align="left" sx={{ color: isDarkMode ? theme.palette.white.main : theme.palette.prime.blue }}>{task.name}</TableCell>
                  <TableCell align="left" sx={{ color: isDarkMode ? theme.palette.white.main : theme.palette.prime.blue }}>{task.department.name}</TableCell>
                  <TableCell align="left" sx={{ color: isDarkMode ? theme.palette.white.main : theme.palette.prime.blue }}>{task.status}</TableCell>
                  <TableCell align="left" sx={{ color: isDarkMode ? theme.palette.white.main : theme.palette.prime.blue }}>{task?.assignee?.displayName}</TableCell>
                </TableRow>

              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhaseProgress;